import React, { useCallback, useState } from 'react';
import type { FeatureCollection, Feature, Polygon } from 'geojson';
import { useMap } from '../map/MapContext';
import { useOgcServices } from '../services/OgcServicesContext';
import { buildChartPdfUrl, buildChartPngPageUrl } from '../config/environment';
import {
  installPlatesOutline,
  removePlatesOutline,
  renderChartPageToCanvas,
  quadCornersFromPolygon,
  addPlateCanvasOverlay,
  removePlateCanvasOverlay,
  removeAllPlateOverlays,
} from '../map/approach-plates';

interface PlateProps {
  apt_ident?: string;
  chart_code?: string;
  chart_name?: string;
  pdf_name?: string;
  cycle?: string;
  quad_confidence?: number;
}

function featureLabel(f: Feature<Polygon, PlateProps>): string {
  const p = f.properties ?? {};
  const parts = [p.apt_ident, p.chart_code, p.chart_name].filter(Boolean);
  return parts.join(' · ') || String(f.id ?? 'chart');
}

function pdfUrl(f: Feature<Polygon, PlateProps>): string | null {
  const { cycle, pdf_name } = f.properties ?? {};
  if (!cycle || !pdf_name) return null;
  return buildChartPdfUrl(cycle, pdf_name);
}

function pngUrl(f: Feature<Polygon, PlateProps>): string | null {
  const { cycle, pdf_name } = f.properties ?? {};
  if (!cycle || !pdf_name) return null;
  return buildChartPngPageUrl(cycle, pdf_name, 1);
}

export const ApproachPlatesPanel: React.FC = () => {
  const { map } = useMap();
  const { featuresClient } = useOgcServices();
  const [plates, setPlates] = useState<FeatureCollection<Polygon, PlateProps> | null>(null);
  const [active, setActive] = useState<Set<string>>(new Set());
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadInView = useCallback(async () => {
    if (!map) return;
    setLoading(true);
    setError(null);
    try {
      const b = map.getBounds();
      const bbox: [number, number, number, number] = [
        b.getWest(), b.getSouth(), b.getEast(), b.getNorth(),
      ];
      const fc = (await featuresClient.searchByBbox('approachPlates', bbox, 500)) as FeatureCollection<Polygon, PlateProps>;
      setPlates(fc);
      installPlatesOutline(map, fc as FeatureCollection);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load approach plates');
    } finally {
      setLoading(false);
    }
  }, [map, featuresClient]);

  const togglePlate = useCallback(async (f: Feature<Polygon, PlateProps>) => {
    if (!map) return;
    const id = String(f.id ?? f.properties?.pdf_name ?? '');
    if (!id) return;
    if (active.has(id)) {
      removePlateCanvasOverlay(map, id);
      setActive((prev) => { const n = new Set(prev); n.delete(id); return n; });
      return;
    }
    const pdf = pdfUrl(f);
    if (!pdf) { setError(`Plate ${id} has no PDF metadata`); return; }
    setPending((prev) => new Set(prev).add(id));
    setError(null);
    try {
      const canvas = await renderChartPageToCanvas({
        pngUrl: pngUrl(f) ?? undefined,
        pdfUrl: pdf,
        pageNumber: 1,
        scale: 2,
        cacheKey: f.properties?.cycle && f.properties?.pdf_name
          ? { cycle: f.properties.cycle, pdfName: f.properties.pdf_name }
          : undefined,
      });
      const corners = quadCornersFromPolygon(f.geometry);
      addPlateCanvasOverlay(map, id, canvas, corners);
      setActive((prev) => new Set(prev).add(id));
    } catch (e) {
      setError(e instanceof Error ? e.message : `Failed to render ${id}`);
    } finally {
      setPending((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }
  }, [map, active]);

  const clearAll = useCallback(() => {
    if (!map) return;
    removeAllPlateOverlays(map);
    removePlatesOutline(map);
    setPlates(null);
    setActive(new Set());
  }, [map]);

  const features = plates?.features ?? [];

  return (
    <div>
      <div className="panel-section">
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-primary" onClick={loadInView} disabled={loading || !map} style={{ flex: 1 }}>
            {loading ? 'Loading…' : '🔎 Load Plates in View'}
          </button>
          {plates && (
            <button className="btn btn-secondary" onClick={clearAll}>Clear</button>
          )}
        </div>
        {error && (
          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--danger, #EF4444)' }}>{error}</div>
        )}
      </div>

      {plates && (
        <div className="panel-section">
          <div className="panel-section-title">
            Plates in view <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({features.length})</span>
          </div>
          {features.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              No georeferenced plates intersect the current view.
            </div>
          )}
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {features.map((f) => {
              const id = String(f.id ?? f.properties?.pdf_name ?? '');
              const isActive = active.has(id);
              const isPending = pending.has(id);
              return (
                <div key={id} className="list-item" style={{ paddingLeft: 4 }}>
                  <label className="toggle" style={{ flex: 1 }}>
                    <span
                      className={`toggle-box ${isActive ? 'checked' : ''}`}
                      onClick={(e) => { e.preventDefault(); togglePlate(f); }}
                    />
                    <span style={{ fontSize: 12 }}>
                      {featureLabel(f)}
                      {isPending && <span style={{ color: 'var(--text-muted)' }}> — rendering…</span>}
                    </span>
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
